import { LoaderCircle, Maximize, Minimize, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { Scrubber } from './Scrubber.tsx';
import type { PlayerState } from './useVideoPlayer.ts';

interface Props {
  state: PlayerState;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onToggleFullscreen: () => void;
  onSeek: (time: number) => void;
  onSeekBack: () => void;
  onSeekForward: () => void;
  /** Tap on the empty video surface — toggles control visibility. */
  onSurfaceTap: () => void;
  /** Any interaction with a control — keeps controls up and resets the timer. */
  onActivity: () => void;
}

/**
 * One control set for both orientations — the landscape layout only scales the
 * circles up (via [data-fullscreen] in CSS); positions stay relative.
 *
 * Layout: a full-area tap layer sits beneath a pointer-events:none controls
 * layer, so taps on empty space toggle visibility while taps on a button act
 * on the button. Controls fade via [data-visible] on both orientations.
 */
export function PlayerControls({
  state,
  onTogglePlay,
  onToggleMute,
  onToggleFullscreen,
  onSeek,
  onSeekBack,
  onSeekForward,
  onSurfaceTap,
  onActivity,
}: Props) {
  return (
    <div className="player-overlay" data-visible={state.controlsVisible ? 'true' : 'false'}>
      {/* Catches taps on empty areas (and every tap while controls are hidden). */}
      <button
        type="button"
        className="player-tap"
        onClick={onSurfaceTap}
        tabIndex={-1}
        aria-label="Показать или скрыть управление"
      />

      {/* Outside .player-controls on purpose: a stall usually happens mid-
          playback, by which time the controls have auto-hidden — and that is
          exactly when the viewer needs to be told it is the network, not a
          freeze. While the controls are up, the play button spins instead. */}
      {state.buffering && !state.controlsVisible && (
        <div className="player-buffering" role="status" aria-label="Загрузка">
          <LoaderCircle size={38} strokeWidth={2.5} />
        </div>
      )}

      <div className="player-controls" onPointerDown={onActivity}>
        <div className="player-overlay__scrim" />

        <button
          type="button"
          className="glass-circle glass-circle--icon player-volume"
          onClick={onToggleMute}
          aria-label={state.muted ? 'Включить звук' : 'Выключить звук'}
        >
          {state.muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
        </button>

        <button
          type="button"
          className="glass-circle glass-circle--icon player-fullscreen"
          onClick={onToggleFullscreen}
          aria-label={state.fullscreen ? 'Выйти из полноэкранного режима' : 'Полный экран'}
        >
          {state.fullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>

        <div className="player-transport">
          {/* Plain text labels, not icons — per the handoff. */}
          <button
            type="button"
            className="glass-circle player-seek player-seek--back"
            onClick={onSeekBack}
            aria-label="Назад на 10 секунд"
          >
            -10
            <br />
            сек
          </button>

          <button
            type="button"
            className="player-play"
            onClick={onTogglePlay}
            aria-label={state.playing ? 'Пауза' : 'Смотреть'}
          >
            {state.buffering ? (
              <LoaderCircle className="player-play__spinner" size={26} strokeWidth={2.5} />
            ) : state.playing ? (
              <Pause size={26} fill="currentColor" />
            ) : (
              <Play size={26} fill="currentColor" />
            )}
          </button>

          <button
            type="button"
            className="glass-circle player-seek player-seek--forward"
            onClick={onSeekForward}
            aria-label="Вперёд на 10 секунд"
          >
            +10
            <br />
            сек
          </button>
        </div>

        <div className="player-scrub">
          <Scrubber
            currentTime={state.currentTime}
            duration={state.duration}
            bufferedTo={state.bufferedTo}
            onSeek={onSeek}
          />
        </div>
      </div>
    </div>
  );
}
